import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  changePersonalBill,
  describePAApiError,
  getSpecialistFeed,
  readMoney,
  startBillsSpecialist,
  type PersonalBill,
  type PersonalBillRecurrence,
  type PersonalBillsSummary,
  type MoneyMonth,
  type PersonalIncome,
} from '../../../lib/api/team.js';
import { hapticSuccess, hapticTap, hapticWarn } from '../../../lib/native.js';
import { useSkillBack } from '../../../lib/skillBack.js';
import { PageShell } from '../../stage/PageShell.js';
import { Skeleton } from '../../ui/Skeleton.js';
import { DinheiroMeses } from './DinheiroMeses.js';
import { MonthsChart, MoneyHero, NextDays, SplitDonut } from './DinheiroVisual.js';
import { IncomeForm, IncomeGlyph, IncomesSection, parseCents } from './Ganhos.js';
import './contas.css';

type BillMood = 'home' | 'energy' | 'connection' | 'card' | 'everyday';

const MOOD_LABEL: Record<BillMood, string> = {
  home: 'Casa',
  energy: 'Luz e gás',
  connection: 'Internet e telefone',
  card: 'Cartão e banco',
  everyday: 'Dia a dia',
};

// Ícone do tipo de conta, em traço (herda a cor de `pgs-ic`).
function BillGlyph({ mood, size = 22 }: { mood: BillMood; size?: number }) {
  return (
    <svg
      className="cnt-glyph"
      style={{ width: size, height: size }}
      viewBox="0 0 40 40"
      aria-hidden="true"
    >
      {mood === 'home' && (
        <>
          <path d="m7 19 13-11 13 11v14H7Z" />
          <path d="M15 33V22h10v11" />
        </>
      )}
      {mood === 'energy' && <path d="m23 4-12 19h9l-3 13 13-20h-9Z" />}
      {mood === 'connection' && (
        <>
          <path d="M7 18c7-7 19-7 26 0M12 23c4-4 12-4 16 0M17 28c2-2 4-2 6 0" />
          <circle cx="20" cy="33" r="2" />
        </>
      )}
      {mood === 'card' && (
        <>
          <rect x="5" y="9" width="30" height="22" rx="5" />
          <path d="M5 16h30M11 25h8" />
        </>
      )}
      {mood === 'everyday' && (
        <>
          <path d="M10 5h20v30l-4-3-4 3-4-3-4 3-4-3Z" />
          <path d="M15 13h10M15 19h10M15 25h6" />
        </>
      )}
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg className="cnt-glyph cnt-check" viewBox="0 0 24 24" aria-hidden="true">
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  );
}

function moodFor(title: string): BillMood {
  const value = title.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  if (/aluguel|condominio|casa|iptu/.test(value)) return 'home';
  if (/luz|energia|gas/.test(value)) return 'energy';
  if (/internet|telefone|celular|wifi/.test(value)) return 'connection';
  if (/cartao|fatura|banco/.test(value)) return 'card';
  return 'everyday';
}

function money(cents: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

function dateLabel(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', timeZone: 'UTC' })
    .format(new Date(`${value}T12:00:00Z`))
    .replace('.', '');
}

function today(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

function distance(value: string): number {
  return Math.round(
    (Date.parse(`${value}T12:00:00Z`) - Date.parse(`${today()}T12:00:00Z`)) / 86_400_000,
  );
}

function dueCopy(value: string): { label: string; tone: string } {
  const days = distance(value);
  if (days < 0)
    return {
      label: `${Math.abs(days)} dia${Math.abs(days) === 1 ? '' : 's'} em atraso`,
      tone: 'late',
    };
  if (days === 0) return { label: 'Vence hoje', tone: 'today' };
  if (days === 1) return { label: 'Vence amanhã', tone: 'soon' };
  if (days <= 7) return { label: `Vence em ${days} dias`, tone: 'soon' };
  return { label: `Vence ${dateLabel(value)}`, tone: 'calm' };
}

function monthName(): string {
  const month = new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(new Date());
  return month.charAt(0).toUpperCase() + month.slice(1);
}

function readData(config: Record<string, unknown> | undefined) {
  const bills = Array.isArray(config?.bills) ? (config.bills as PersonalBill[]) : [];
  const fallback: PersonalBillsSummary = {
    openCount: 0,
    overdueCount: 0,
    dueThisWeekCount: 0,
    openTotalCents: 0,
    dueThisWeekTotalCents: 0,
    nextDueDate: null,
  };
  const summary =
    config?.summary && typeof config.summary === 'object'
      ? (config.summary as PersonalBillsSummary)
      : fallback;
  return { configured: config?.configured === true, bills, summary };
}

// Primeiro contato: o que eu faço com as contas e um botão para começar.
function Welcome({ onBack, onStart }: { onBack: () => void; onStart: () => void }) {
  return (
    <PageShell
      kicker="Dinheiro"
      title="Eu lembro. Você decide."
      lead="Deixo à vista o que entra e o que vence, sem sustos, e o controle continua com você."
      onBack={onBack}
    >
      <section className="pgs-section">
        <div className="pgs-row">
          <span className="pgs-ic">
            <BillGlyph mood="everyday" />
          </span>
          <span className="pgs-rowt">
            <b>Tudo na ordem em que vence</b>
            <small>Eu organizo os vencimentos e mostro o que vem primeiro.</small>
          </span>
        </div>
        <div className="pgs-row">
          <span className="pgs-ic">
            <BillGlyph mood="home" />
          </span>
          <span className="pgs-rowt">
            <b>A próxima já fica pronta</b>
            <small>Conta que se repete todo mês, eu preparo de novo.</small>
          </span>
        </div>
        <div className="pgs-row">
          <span className="pgs-ic">
            <IncomeGlyph category="salario" />
          </span>
          <span className="pgs-rowt">
            <b>O que entra também</b>
            <small>Salário, freela e o que mais cair, com a sobra prevista do mês.</small>
          </span>
        </div>
        <div className="pgs-row">
          <span className="pgs-ic">
            <BillGlyph mood="card" />
          </span>
          <span className="pgs-rowt">
            <b>O pagamento é com você</b>
            <small>
              Eu organizo o que você informou. Para pagar, use o aplicativo do seu banco.
            </small>
          </span>
        </div>
      </section>
      <div className="pgs-actions">
        <button
          type="button"
          className="pgs-btn solid"
          onClick={() => {
            void hapticTap();
            onStart();
          }}
        >
          Organizar meu dinheiro
        </button>
      </div>
    </PageShell>
  );
}

function BillForm({
  first = false,
  bill,
  onCancel,
  onSaved,
}: {
  first?: boolean | undefined;
  /** Conta já anotada: o formulário abre preenchido e guarda as mudanças nela. */
  bill?: PersonalBill | undefined;
  onCancel?: () => void;
  onSaved: () => Promise<void>;
}) {
  const editing = Boolean(bill);
  const [title, setTitle] = useState(bill?.title ?? '');
  const [amount, setAmount] = useState(
    bill ? (bill.amountCents / 100).toFixed(2).replace('.', ',') : '',
  );
  const [dueDate, setDueDate] = useState(bill?.dueDate ?? '');
  const [recurrence, setRecurrence] = useState<PersonalBillRecurrence>(
    bill?.recurrence ?? 'monthly',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const amountCents = parseCents(amount);

  async function save() {
    if (
      title.trim().length < 2 ||
      !Number.isSafeInteger(amountCents) ||
      amountCents < 1 ||
      !dueDate
    ) {
      setError('Preencha nome, valor e vencimento para continuar.');
      void hapticWarn();
      return;
    }
    setSaving(true);
    setError('');
    try {
      await changePersonalBill(
        bill
          ? { type: 'update', id: bill.id, title: title.trim(), amountCents, dueDate, recurrence }
          : { type: 'add', title: title.trim(), amountCents, dueDate, recurrence },
      );
      hapticSuccess();
      await onSaved();
    } catch (err) {
      setError(
        describePAApiError(err, 'Não consegui guardar esta conta agora. Tenta mais uma vez?'),
      );
      setSaving(false);
    }
  }

  return (
    <section className="pgs-section cnt-form">
      {!first && <h2 className="pgs-h2">{editing ? 'Mudar esta conta' : 'Nova conta'}</h2>}
      <label className="cnt-field">
        <span>Nome da conta</span>
        <span className="cnt-input">
          <BillGlyph mood={moodFor(title)} size={22} />
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Ex.: Internet de casa"
            maxLength={80}
          />
        </span>
      </label>
      <div className="cnt-pair">
        <label className="cnt-field">
          <span>Valor</span>
          <span className="cnt-input">
            <b>R$</b>
            <input
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputMode="decimal"
              placeholder="129,90"
            />
          </span>
        </label>
        <label className="cnt-field">
          <span>Vencimento</span>
          <span className="cnt-input">
            <input
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              type="date"
            />
          </span>
        </label>
      </div>
      <span className="cnt-label">Ela se repete?</span>
      <div className="pgs-chips" role="group" aria-label="Ela se repete?">
        <button
          type="button"
          className={`pgs-chip${recurrence === 'monthly' ? ' on' : ''}`}
          aria-pressed={recurrence === 'monthly'}
          onClick={() => setRecurrence('monthly')}
        >
          Todo mês
        </button>
        <button
          type="button"
          className={`pgs-chip${recurrence === 'none' ? ' on' : ''}`}
          aria-pressed={recurrence === 'none'}
          onClick={() => setRecurrence('none')}
        >
          Só desta vez
        </button>
      </div>
      <p className="cnt-hint">
        {recurrence === 'monthly' ? 'Eu preparo a próxima a cada mês.' : 'Um único vencimento.'}
      </p>
      {error && (
        <p className="cnt-error" role="alert">
          {error}
        </p>
      )}
      <div className="pgs-actions">
        {onCancel && (
          <button type="button" className="pgs-btn" onClick={onCancel}>
            Agora não
          </button>
        )}
        <button type="button" className="pgs-btn solid" disabled={saving} onClick={save}>
          {saving ? 'Guardando…' : editing ? 'Guardar' : 'Guardar conta'}
        </button>
      </div>
    </section>
  );
}

function Dashboard({
  bills,
  summary,
  incomes,
  labels,
  moneyMonth,
  history,
  onBack,
  onChanged,
  onMonths,
}: {
  bills: PersonalBill[];
  summary: PersonalBillsSummary;
  incomes: PersonalIncome[];
  labels: string[];
  moneyMonth: MoneyMonth | null;
  /** Os últimos meses (o gráfico da página); vazio em servidor antigo. */
  history: MoneyMonth[];
  onBack: () => void;
  onChanged: () => Promise<void>;
  /** Abre os últimos meses (só com servidor que manda o histórico). */
  onMonths?: (() => void) | undefined;
}) {
  const [adding, setAdding] = useState(false);
  const [addingIncome, setAddingIncome] = useState(false);
  // Conta aberta para mudar valor, vencimento ou nome (o formulário entra no lugar da linha).
  const [editingBill, setEditingBill] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [justPaid, setJustPaid] = useState<string | null>(null);
  // Apagar pede um segundo toque; um erro numa ação aparece na página.
  const [confirming, setConfirming] = useState<string | null>(null);
  const [failed, setFailed] = useState('');
  const active = bills.filter((bill) => bill.status === 'open');
  const paid = bills.filter((bill) => bill.status === 'paid').slice(0, 5);
  const month = monthName();
  const empty = !active.length && !paid.length && !incomes.length;
  const noBills = !active.length && !paid.length;

  async function act(id: string, type: 'mark_paid' | 'reopen' | 'delete') {
    setBusy(id);
    setFailed('');
    try {
      await changePersonalBill({ type, id });
      if (type === 'mark_paid') {
        setJustPaid(id);
        hapticSuccess();
        setTimeout(() => setJustPaid(null), 1200);
      } else void hapticTap();
      await onChanged();
    } catch (err) {
      setFailed(describePAApiError(err, 'Não consegui mudar essa conta agora. Tente de novo.'));
      void hapticWarn();
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  }

  // Como o que está em aberto se divide por tipo de conta (barras do raio-x).
  const totals = new Map<BillMood, number>();
  for (const bill of active) {
    const mood = moodFor(bill.title);
    totals.set(mood, (totals.get(mood) ?? 0) + bill.amountCents);
  }
  const openSum = [...totals.values()].reduce((sum, value) => sum + value, 0);
  const groups = [...totals.entries()]
    .map(([mood, cents]) => ({
      mood,
      cents,
      share: openSum > 0 ? Math.max(4, Math.round((cents / openSum) * 100)) : 0,
    }))
    .sort((a, b) => b.cents - a.cents);

  const overdue = summary.overdueCount;
  const overdueLine =
    overdue === 1
      ? '1 conta merece atenção primeiro.'
      : `${overdue} contas merecem atenção primeiro.`;
  const title = empty
    ? 'Nenhuma conta à vista'
    : overdue > 0
      ? `${month} pede atenção`
      : active.length
        ? `${month} em ordem`
        : `${month} em dia`;
  const lead = empty
    ? 'Me conte o que entra e o que vence. Eu coloco tudo em ordem aqui.'
    : active.length
      ? 'O que entra e o que vence, na ordem em que chegam.'
      : undefined;

  // O número da página: o que vence na semana; sem nada na semana, o total em aberto.
  const weekCount = summary.dueThisWeekCount;
  const openCount = summary.openCount;
  const figure =
    weekCount > 0
      ? {
          value: money(summary.dueThisWeekTotalCents),
          note: `${weekCount === 1 ? '1 conta vence' : `${weekCount} contas vencem`} em 7 dias`,
        }
      : {
          value: money(summary.openTotalCents),
          note: `em aberto em ${openCount === 1 ? '1 conta' : `${openCount} contas`}, nada vence em 7 dias`,
        };

  return (
    <PageShell kicker="Dinheiro" title={title} lead={lead} onBack={onBack}>
      {moneyMonth && !empty ? (
        <MoneyHero month={moneyMonth} />
      ) : (
        active.length > 0 && (
          <div className="pgs-figure">
            <b>{figure.value}</b>
            <small>{figure.note}</small>
          </div>
        )
      )}

      {!adding && !addingIncome && (
        <div className="din-quick">
          <button
            type="button"
            className="pgs-btn"
            onClick={() => {
              setAddingIncome(true);
              void hapticTap();
            }}
          >
            Anotar ganho
          </button>
          <button
            type="button"
            className="pgs-btn"
            onClick={() => {
              setAdding(true);
              void hapticTap();
            }}
          >
            Anotar conta
          </button>
        </div>
      )}

      {!empty && !adding && !addingIncome && <NextDays bills={bills} incomes={incomes} />}

      {!empty && !adding && !addingIncome && history.length > 1 && (
        <MonthsChart history={history} onMore={onMonths} />
      )}
      {onMonths && !empty && !adding && !addingIncome && history.length <= 1 && (
        <button type="button" className="pgs-row din-months" onClick={onMonths}>
          <span className="pgs-rowt">
            <b>Seus últimos meses</b>
            <small>Quanto sobrou em cada um, a média e a planilha.</small>
          </span>
          <span className="din-go" aria-hidden="true">
            ›
          </span>
        </button>
      )}

      {empty && !adding && !addingIncome && (
        <div className="pgs-note">
          <i>+</i>
          <span>
            <b>Comece pelo salário ou pela conta que vence primeiro</b>
            <small>Nome, valor e data. Leva alguns segundos.</small>
          </span>
        </div>
      )}

      {!noBills && (
        <div className="pgs-note">
          <i>{overdue > 0 ? '!' : '✓'}</i>
          <span>
            <b>
              {overdue > 0
                ? overdueLine
                : active.length
                  ? 'Seus próximos passos estão claros.'
                  : 'Você concluiu tudo que estava aberto.'}
            </b>
            <small>
              {overdue > 0
                ? 'Deixei em destaque na lista. Quando pagar, é só tocar em Paguei.'
                : active.length
                  ? summary.nextDueDate
                    ? `A próxima vence ${dateLabel(summary.nextDueDate)}.`
                    : 'Quando resolver uma, toque em Paguei.'
                  : 'Adicione a próxima quando ela chegar.'}
            </small>
          </span>
        </div>
      )}

      {addingIncome && (
        <IncomeForm
          labels={labels}
          onCancel={() => setAddingIncome(false)}
          onSaved={async () => {
            setAddingIncome(false);
            await onChanged();
          }}
        />
      )}

      {adding && (
        <BillForm
          onCancel={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false);
            await onChanged();
          }}
        />
      )}

      {failed && (
        <p className="cnt-error" role="alert">
          {failed}
        </p>
      )}

      {active.length > 0 && (
        <section className="pgs-section">
          <h2 className="pgs-h2">
            Próximos vencimentos <small>{active.length}</small>
          </h2>
          {active.map((bill) => {
            if (editingBill === bill.id)
              return (
                <BillForm
                  key={bill.id}
                  bill={bill}
                  onCancel={() => setEditingBill(null)}
                  onSaved={async () => {
                    setEditingBill(null);
                    await onChanged();
                  }}
                />
              );
            const due = dueCopy(bill.dueDate);
            const mood = moodFor(bill.title);
            const repeat =
              bill.recurrence === 'monthly'
                ? 'todo mês'
                : due.tone === 'calm'
                  ? 'só desta vez'
                  : dateLabel(bill.dueDate);
            const rowClass = [
              'pgs-row cnt-bill',
              due.tone === 'late' ? 'warn' : '',
              justPaid === bill.id ? 'paid-now' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div key={bill.id} className={rowClass}>
                <span className="pgs-ic">
                  <BillGlyph mood={mood} />
                </span>
                <span className="pgs-rowt">
                  <b>{bill.title}</b>
                  <small>
                    <em className={`cnt-due ${due.tone}`}>{due.label}</em> · {repeat}
                  </small>
                </span>
                <b className="pgs-val">{money(bill.amountCents)}</b>
                <div className="cnt-acts">
                  <button
                    type="button"
                    className="pgs-btn small"
                    disabled={busy === bill.id}
                    onClick={() => act(bill.id, 'mark_paid')}
                  >
                    Paguei
                  </button>
                  <button
                    type="button"
                    className="pgs-btn small cnt-edit"
                    disabled={busy === bill.id}
                    onClick={() => {
                      setAdding(false);
                      setAddingIncome(false);
                      setEditingBill(bill.id);
                      void hapticTap();
                    }}
                    aria-label={`Mudar ${bill.title}`}
                  >
                    Mudar
                  </button>
                  <button
                    type="button"
                    className={`pgs-btn small cnt-drop${confirming === bill.id ? ' sure' : ''}`}
                    disabled={busy === bill.id}
                    onClick={() =>
                      confirming === bill.id ? act(bill.id, 'delete') : setConfirming(bill.id)
                    }
                    aria-label={
                      confirming === bill.id ? `Apagar mesmo ${bill.title}` : `Apagar ${bill.title}`
                    }
                  >
                    {confirming === bill.id ? 'Apagar mesmo' : 'Apagar'}
                  </button>
                </div>
              </div>
            );
          })}
        </section>
      )}

      <IncomesSection
        incomes={incomes}
        labels={labels}
        month={moneyMonth?.month ?? null}
        onChanged={onChanged}
        setAdding={setAddingIncome}
      />

      <SplitDonut
        groups={groups.map((group) => ({ label: MOOD_LABEL[group.mood], cents: group.cents }))}
      />

      {paid.length > 0 && (
        <section className="pgs-section">
          <h2 className="pgs-h2">
            Concluídas <small>{paid.length}</small>
          </h2>
          {paid.map((bill) => (
            <div key={bill.id} className="pgs-row done cnt-done">
              <span className="pgs-ic">
                <CheckGlyph />
              </span>
              <span className="pgs-rowt">
                <b>{bill.title}</b>
                <small>
                  {money(bill.amountCents)} · venceu {dateLabel(bill.dueDate)}
                </small>
              </span>
              <button
                type="button"
                className="pgs-btn small"
                disabled={busy === bill.id}
                onClick={() => act(bill.id, 'reopen')}
              >
                Desfazer
              </button>
            </div>
          ))}
        </section>
      )}

      {!empty && (
        <div className="pgs-note">
          <i>“</i>
          <span>
            <b>É só me perguntar</b>
            <small>“Caiu meu salário”, “O que vence esta semana?” ou “Quanto sobra no mês?”</small>
          </span>
        </div>
      )}

      <p className="cnt-trust">
        Eu organizo o que você informou, sem acesso ao seu banco. Para pagar, confira os dados no
        aplicativo do seu banco.
      </p>
    </PageShell>
  );
}

export function ContasView() {
  const back = useSkillBack();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['pa-specialist-feed', 'contas'],
    queryFn: () => getSpecialistFeed('contas'),
  });
  const [started, setStarted] = useState(false);
  // Os últimos meses abrem direto quando o cartão "o mês fechou" do Hoje traz ?ver=meses.
  const [months, setMonths] = useState(
    () => new URLSearchParams(window.location.search).get('ver') === 'meses',
  );
  const data = useMemo(() => readData(query.data?.config), [query.data?.config]);
  const moneyData = useMemo(() => readMoney(query.data?.config), [query.data?.config]);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'contas'] });
    // O Hoje (cartões e o selo "1 atrasada" do atalho) e o painel do palco leem a mesma
    // lista: apagar, pagar ou adiar aqui tem que sumir de lá na hora.
    void qc.invalidateQueries({ queryKey: ['today'] });
    void qc.invalidateQueries({ queryKey: ['stage-bills'] });
    void qc.invalidateQueries({ queryKey: ['trail'] });
    await query.refetch();
  };

  if (query.isLoading)
    return (
      <PageShell kicker="Dinheiro" title="Abrindo o seu dinheiro" onBack={back}>
        <Skeleton variant="rows" label="Abrindo o seu dinheiro" />
      </PageShell>
    );

  if (query.isError || !query.data)
    return (
      <PageShell
        kicker="Dinheiro"
        title="Não consegui abrir o seu dinheiro agora"
        lead="Tento de novo quando você quiser."
        onBack={back}
      >
        <div className="pgs-actions">
          <button type="button" className="pgs-btn solid" onClick={() => query.refetch()}>
            Tentar novamente
          </button>
        </div>
      </PageShell>
    );

  if (!data.configured && !started)
    return <Welcome onBack={back} onStart={() => setStarted(true)} />;

  if (!data.configured && started)
    return (
      <PageShell
        kicker="Dinheiro"
        title="Qual vencimento eu guardo primeiro?"
        lead="Leva alguns segundos. Depois, é só me perguntar."
        onBack={back}
      >
        <BillForm
          first
          onCancel={async () => {
            await startBillsSpecialist();
            await refresh();
            setStarted(false);
          }}
          onSaved={async () => {
            await refresh();
            setStarted(false);
          }}
        />
      </PageShell>
    );

  if (months && moneyData.history.length > 0)
    return (
      <DinheiroMeses
        history={moneyData.history}
        bills={data.bills}
        incomes={moneyData.incomes}
        onBack={() => setMonths(false)}
      />
    );

  return (
    <Dashboard
      bills={data.bills}
      summary={data.summary}
      incomes={moneyData.incomes}
      labels={moneyData.labels}
      moneyMonth={moneyData.month}
      history={moneyData.history}
      onBack={back}
      onChanged={refresh}
      onMonths={moneyData.history.length > 0 ? () => setMonths(true) : undefined}
    />
  );
}
